import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { get } from 'underscore';
import { CouponTeachingBubbleRenderer } from '@bingads-webui-cc-react/coupon-teachingbubble-loader';
import { useReportingInsightsTeachingBubble } from '@bingads-webui-campaign-react/reporting-insights-components';
import {
  triggerMultiStepTeachingBubbleWithLock,
  MultiStepTeachingBubbleContext,
} from '@bingads-webui-campaign-react/multi-step-teaching-bubble';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { PageReady } from '@bingads-webui/page-ready';
import { getParameter } from '@bingads-webui/url-util';

import {
  escMigrationTeachingBubbleFeatureName,
  uetConsentNotificationFeatureName,
  adsStudioTeachingBubbleFeatureName,
} from './teaching-bubble-config';
import { withCcJsDataContextProvider } from '../../contexts/with-cc-js-data-context-provider';
import { churnModelCouponNotificationFeatureName } from './custom-churn-model-coupon-teaching-bubble-config';

export const TeachingBubbleRendererBase = ({
  aid,
  cid,
  permissions,
  isCouponEnabled,
}) => {
  const bubbleContext = useContext(MultiStepTeachingBubbleContext);

  // Esc Migration bubble
  const [hasReadMigrationFlag, setHasReadMigrationFlag] = useState(false);
  const [isMigrationUser, setIsMigrationUser] = useState('false');
  useEffect(() => {
    if (!hasReadMigrationFlag && aid && cid) {
      setHasReadMigrationFlag(true);
      edmCampaign
        .Customers.$withKey(cid)
        .Accounts.$withKey(aid)
        .get()
        .toPromise()
        .then((account) => {
          setIsMigrationUser(account && account.MigratedFromESC);
        });
    }
  }, [hasReadMigrationFlag, aid, cid]);

  useEffect(() => {
    if (isMigrationUser === 'true') {
      triggerMultiStepTeachingBubbleWithLock({
        ...bubbleContext,
        featureName: escMigrationTeachingBubbleFeatureName,
      });
    }
  }, [bubbleContext, isMigrationUser]);


  // UET Consent notification
  // Please DO NOT follow this example to trigger teaching callout, timeout logic is specially in place
  // to handle the re-construct of global header when page layout changes. E.G. Redline Banner shows up.
  useEffect(() => {
    PageReady.afterPageReady().then(() => {
      setTimeout(() => {
        const isUETConsentNotificationEnabled = get(permissions, ['dynamic', 'UETConsentNotification'], false);
        if (isUETConsentNotificationEnabled) {
          triggerMultiStepTeachingBubbleWithLock({
            ...bubbleContext,
            featureName: uetConsentNotificationFeatureName,
          });
        }
      }, 10000);
    });
  }, [bubbleContext, permissions]);

  useReportingInsightsTeachingBubble(permissions);

  useEffect(() => {
    const isChurnModelCouponNotificationEnabled = get(permissions, ['dynamic', 'ChurnModelCouponNotification'], false);
    if (isChurnModelCouponNotificationEnabled) {
      triggerMultiStepTeachingBubbleWithLock({
        ...bubbleContext,
        featureName: churnModelCouponNotificationFeatureName,
      });
    }
  }, [bubbleContext, permissions]);

  useEffect(() => {
    const isAdsStudioMVPEnabled = get(permissions, ['dynamic', 'AdsStudioMVP'], false);
    const ise2etest = getParameter('ise2etest') === 'true';
    const isselenium = getParameter('isselenium') === 'true'; // For some C# tests
    const showForTest = getParameter('showAdsStudioTeachingBubble') === 'true';
    if (isAdsStudioMVPEnabled && ((!ise2etest && !isselenium) || showForTest)) {
      triggerMultiStepTeachingBubbleWithLock({
        ...bubbleContext,
        featureName: adsStudioTeachingBubbleFeatureName,
      });
    }
  }, [bubbleContext, permissions]);

  if (isCouponEnabled) {
    return <CouponTeachingBubbleRenderer aid={aid} permissions={permissions} />;
  }

  return null;
};

TeachingBubbleRendererBase.propTypes = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  permissions: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.objectOf(PropTypes.bool),
    PropTypes.number,
  ])).isRequired,
  isCouponEnabled: PropTypes.bool.isRequired,
};

export const TeachingBubbleRenderer = withCcJsDataContextProvider(TeachingBubbleRendererBase);
